import { useCallback, useEffect } from 'react';
import { create } from 'zustand';

import { useCurrentUser } from '@bonusly/who-am-i';

import { useUserContentDismissal } from '../../../components/profile/user-content-dismissal';

const STORAGE_KEY_PREFIX = 'bonusly:team-panel-seen:';
const DISMISSAL_NAME = 'team-panel-badge';
const DISMISSAL_NAMESPACE = 'team';

const readLocal = (key: string): boolean => {
	try {
		return window.localStorage.getItem(key) === '1';
	} catch {
		return false;
	}
};

const writeLocal = (key: string): void => {
	try {
		window.localStorage.setItem(key, '1');
	} catch {
		// Ignore storage failures (private mode, quota, etc).
	}
};

type BadgeDismissedStore = {
	dismissed: boolean;
	initializedForKey: string | null;
	initialize: (key: string) => void;
	dismiss: (key: string) => void;
};

const useBadgeDismissedStore = create<BadgeDismissedStore>((set) => ({
	dismissed: false,
	initializedForKey: null,
	initialize: (key: string) =>
		set((state) => {
			if (state.initializedForKey === key) return state;
			return { dismissed: readLocal(key), initializedForKey: key };
		}),
	dismiss: (key: string) => {
		writeLocal(key);
		set({ dismissed: true });
	},
}));

export const useTeamPanelBadge = () => {
	const user = useCurrentUser();
	const storageKey = `${STORAGE_KEY_PREFIX}${user.id}`;

	const { dismissed: localDismissed, initialize, dismiss } = useBadgeDismissedStore();
	initialize(storageKey);

	const { data: serverDismissal, updateUserContentDismissal } = useUserContentDismissal({
		name: DISMISSAL_NAME,
		namespace: DISMISSAL_NAMESPACE,
		enabled: !localDismissed,
	});

	const dismissed = localDismissed || !!serverDismissal?.dismissed_at;

	useEffect(() => {
		if (!localDismissed && serverDismissal?.dismissed_at) {
			dismiss(storageKey);
		}
	}, [localDismissed, serverDismissal?.dismissed_at, storageKey, dismiss]);

	const markAsSeen = useCallback(() => {
		if (dismissed) return;
		dismiss(storageKey);
		updateUserContentDismissal();
	}, [dismissed, storageKey, dismiss, updateUserContentDismissal]);

	return { showBadge: !dismissed, markAsSeen };
};
